import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Divider, // Added Divider
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { TaskForm } from '@/organism/BossBaby';
import {
  CreateBossBabyTaskDto,
  UpdateBossBabyTaskDto,
  BossBabyTaskDto
} from '@/dto/BossBabyDto';

interface TaskFormTemplateProps {
  initialData?: BossBabyTaskDto;
  onSubmit: (data: CreateBossBabyTaskDto | UpdateBossBabyTaskDto) => void;
  onCancel: () => void;
  isEdit?: boolean;
  loading?: boolean;
}

export const TaskFormTemplate: React.FC<TaskFormTemplateProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
  loading = false,
}) => {
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
            Boss Baby Tasks
          </Link>
          <Typography color="text.primary">
            {isEdit ? 'Edit Task' : 'Create Task'}
          </Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ mb: 1 }}>
          {isEdit ? `Edit Task: ${initialData?.taskName}` : 'Create New Boss Baby Task'}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {isEdit
            ? 'Modify the task configuration and execution settings'
            : 'Configure a new Boss Baby task with its workflow and parameters'
          }
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} /> {/* Added Divider */}

      {/* Form Content */}
      {loading ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading task data...</Typography>
        </Paper>
      ) : (
        <Box> {/* Wrapped TaskForm in a Box */}
          <TaskForm
            initialData={initialData}
            onSubmit={onSubmit}
            onCancel={onCancel}
            isEdit={isEdit}
          />
        </Box>
      )}

      {/* Help Text */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Boss Baby Task Guidelines
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Task Name:</strong> Use a hierarchical naming convention.
          Examples: BossBaby.Schedule.Create, BossBaby.Email.Send, BossBaby.Calendar.Update.
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>🤖 LLM Prompt (The Brain):</strong> This is the most critical part.
          It instructs the AI on <strong>what to do</strong> and <strong>how to use the tools</strong>.
          <br />
          Example: <em>"When the user asks to schedule a meeting, extract the 5W1H info. If info is missing, ask the user. Once you have all info, use the ScheduleManagementTool."</em>
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>🛠️ Tools (The Hands):</strong> Select the tools that this task will use during execution.
          The AI cannot use a tool unless it is selected here.
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Priority Levels:</strong>
          • <strong>HIGH:</strong> Critical tasks that need immediate processing
          • <strong>MEDIUM:</strong> Standard tasks with normal priority
          • <strong>LOW:</strong> Background tasks that can be delayed
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Execution Frequency:</strong>
          • <strong>DAILY:</strong> Tasks that can be executed multiple times per day
          • <strong>WEEKLY:</strong> Tasks with weekly execution limits
          • <strong>MONTHLY:</strong> Tasks with monthly execution limits
        </Typography>

        <Typography variant="body2" paragraph>
          <strong>Advanced Config:</strong> JSON configuration for execution parameters like maxRetries, timeout, and auto-execution settings.
        </Typography>

        <Typography variant="body2">
          <strong>Examples:</strong> Provide clear examples of user messages and descriptions
          to help users understand when and how this task should be triggered.
        </Typography>
      </Paper>
    </Box>
  );
};